import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import type PlainDate from '@Domain/PlainDate';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import type InternalInterviewConsentView from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/InternalInterviewConsentView';
import type RecordingView from '@UI/Store/Interviews/Views/RecordingView';
import type LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import type TranscriptView from '@UI/Store/Interviews/Views/TranscriptView';
import type TranscriptionIssueView from '@UI/Store/WorkflowRuns/Views/TranscriptionIssueView';

type Args = {
	readonly origin: 'server' | 'local';
	readonly kind: InterviewKind;
	readonly interviewId: number | null;
	readonly localInterviewId: string | null;
	readonly conductedOn: PlainDate | null;
	readonly name: string | null;
	readonly participants: readonly InternalInterviewParticipantView[];
	readonly consent: InternalInterviewConsentView | null;
	readonly outboxItem: OutboxItem | null;
	readonly recording: RecordingView | null;
	readonly preparationId: number | null;
	readonly transcriptionIssue: TranscriptionIssueView | null;
	readonly processingState: LabeledValueView | null;
};

class InternalInterviewDetailsView {
	readonly origin: 'server' | 'local';
	/** Falls back to `CONVERSATION` when neither the server row nor the local record carries a type. */
	readonly kind: InterviewKind;
	readonly interviewId: number | null;
	readonly localInterviewId: string | null;
	readonly conductedOn: PlainDate | null;
	/** `null` on a row predating the required name (A9), or a local subject carrying none yet. */
	readonly name: string | null;
	readonly participants: readonly InternalInterviewParticipantView[];
	readonly consent: InternalInterviewConsentView | null;
	/** The queue item on THIS device, when one is known; `null` otherwise — never an invention. */
	readonly outboxItem: OutboxItem | null;
	readonly recording: RecordingView | null;
	/** The preparation where the transcription is followed (`InterviewView.preparationId`) — `null` on a
	 * purely local subject, which has no server `InterviewView` to read it from. */
	readonly preparationId: number | null;
	/** The failing or blocked transcription step; `null` when there is none and on a purely local subject. */
	readonly transcriptionIssue: TranscriptionIssueView | null;
	/** The server's reading of the interview's treatment; `null` on a purely local subject. */
	readonly processingState: LabeledValueView | null;
	/** The server's verdict on deletion; `false` on a purely local subject, which has nothing to delete server-side. */

	constructor(parts: Args) {
		this.origin = parts.origin;
		this.kind = parts.kind;
		this.interviewId = parts.interviewId;
		this.localInterviewId = parts.localInterviewId;
		this.conductedOn = parts.conductedOn;
		this.name = parts.name;
		this.participants = parts.participants;
		this.consent = parts.consent;
		this.outboxItem = parts.outboxItem;
		this.recording = parts.recording;
		this.preparationId = parts.preparationId;
		this.transcriptionIssue = parts.transcriptionIssue;
		this.processingState = parts.processingState;

		Object.freeze(this);
	}

	/** A subject whose date is unknown still renders a title: the screen never shows an empty heading. */
	get conductedOnDisplay(): string {
		return this.conductedOn?.toDisplay() ?? 'Date inconnue';
	}

	/** Whether `title` renders the name rather than falling back to the date (A9). */
	get hasName(): boolean {
		return (this.name?.trim() ?? '') !== '';
	}

	/** The screen's heading: the name when the interview carries one, the date otherwise (A9). */
	get title(): string {
		const trimmedName = this.name?.trim() ?? '';

		return trimmedName !== '' ? trimmedName : this.conductedOnDisplay;
	}

	/**
	 * The date to show as `PageHeader`'s subtitle — only when `title` already carries the name, so the
	 * date is never rendered twice. `null` when `title` is already the date: the caller turns this into
	 * `undefined` at the call site, `PageHeader.description` being what a caller omits, not a value read.
	 */
	get dateSubtitle(): string | null {
		return this.hasName ? this.conductedOnDisplay : null;
	}

	get isConsentWithdrawn(): boolean {
		return this.consent?.isWithdrawn === true;
	}

	/** The interview is an entered text: it has a transcript but never an audio recording. */
	get isTextEntry(): boolean {
		return this.kind === InterviewKind.TEXT;
	}

	/** Whether a transcript exists or will: an entered text carries it from creation, a recording once stored. */
	get hasTranscriptSource(): boolean {
		return this.isTextEntry || this.recordingStatus === 'stored';
	}

	/** Any status other than `stored` means the recording is still on its way up. */
	get recordingStatus(): 'stored' | 'uploading' | null {
		if (this.recording === null) {
			return null;
		}

		return this.recording.status.value === 'stored' ? 'stored' : 'uploading';
	}

	/**
	 * The store's transcript slice is global — it holds whichever transcript was fetched last. A screen
	 * asks this before mounting anything on it, or it renders another subject's transcribed speech.
	 */
	ownsTranscript(transcript: TranscriptView | null): boolean {
		return (
			transcript !== null && this.interviewId !== null && transcript.interviewId === this.interviewId
		);
	}
}

export default InternalInterviewDetailsView;
